"use client";

import { Button } from "@shared/src/components/ui/button";
import { IoClose, IoChevronForward, IoSettingsSharp } from "react-icons/io5";
import { RiDropFill } from "react-icons/ri";
import { GoldBarsIcon } from "@shared/src/components/assets/icons";
import { DrawerContent, DrawerClose, DrawerHeader, DrawerTitle } from "@shared/src/components/ui/drawer";
import Link from "next/link";
import { IconType } from "react-icons";
import { FaucetModal } from "@shared/src/components/common/dialogs/FaucetModal";
import React, { useEffect, useState, useCallback } from "react";
import { UserAccountModal } from "@shared/src/components/common/dialogs/UserAccountModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@shared/src/components/ui/dropdown-menu";
import { additionalNavItems, NavigationItem, orderHistoryNavigation } from "@shared/src/config/navigation";
import { usePathname, useRouter } from "next/navigation";
import { useMediaQuery } from "@shared/src/hooks/useMediaQuery";
import { useAuthStore } from "@shared/src/stores/useAuthStore";
import { shortenAddress } from "@shared/src/utils/helpers";
import { NetworkAvatar } from "@shared/src/components/common/avatars/NetworkAvatar";
import { useGoldBars } from "@shared/src/hooks/useGoldBars";
import { Skeleton } from "@shared/src/components/ui/skeleton";
import { IS_MAINNET } from "@shared/src/constants";

interface MoreDrawerProps {
  navigationItems: {
    other: NavigationItem[];
    account: NavigationItem[];
  };
  onOpenChange: (open: boolean) => void;
}

interface IconComponent extends IconType {
  (props: { size?: number; className?: string }): React.ReactElement;
}

export function MoreDrawer({ navigationItems, onOpenChange }: MoreDrawerProps) {
  const [isFaucetOpen, setIsFaucetOpen] = useState(false);
  const [isUserAccountOpen, setIsUserAccountOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const isAboveMd = useMediaQuery("(min-width: 768px)");
  const { activeAccount } = useAuthStore();
  const { goldBars, isLoading } = useGoldBars();

  // Close drawer when pathname changes or screen size goes above md
  useEffect(() => {
    if (pathname || isAboveMd) {
      onOpenChange(false);
    }
  }, [pathname, isAboveMd, onOpenChange]);

  const renderIcon = (Icon: IconType, size: number = 24) => {
    // Handle both react-icons and our custom icons
    const IconComponent = Icon as IconComponent;
    return <IconComponent size={size} className="text-text-secondary" />;
  };

  const closeAllModals = useCallback(() => {
    setOpenDropdown(null);
    setIsFaucetOpen(false);
    setIsUserAccountOpen(false);
    if (onOpenChange) {
      onOpenChange(false);
    }
  }, [onOpenChange]);

  const handleMenuItemClick = () => {
    closeAllModals();
  };

  return (
    <DrawerContent className="h-[100dvh] bg-surfaces-bg-primary">
      <DrawerHeader className="sr-only">
        <DrawerTitle>Navigation Menu</DrawerTitle>
      </DrawerHeader>
      <div className="flex flex-col h-[100dvh] overflow-hidden">
        {/* Header Section */}
        <div className="flex items-center justify-between px-8 py-3 flex-shrink-0">
          <DrawerClose asChild>
            <Button variant="ghost-clean" size="icon" onClick={handleMenuItemClick}>
              <IoClose size={24} />
            </Button>
          </DrawerClose>
          <div className="flex items-center gap-4">
            <Link href="/settings" onClick={closeAllModals}>
              <Button variant="ghost-clean" size="icon">
                <IoSettingsSharp size={24} />
              </Button>
            </Link>
          </div>
        </div>

        {/* User Profile Section */}
        <div className="px-6 flex-shrink-0">
          <div
            className="flex items-center justify-between p-6 bg-surfaces-bg-secondary rounded-2xl cursor-pointer hover:opacity-75"
            onClick={() => {
              setIsUserAccountOpen(true);
              setOpenDropdown(null);
            }}
          >
            <div className="flex items-center gap-4">
              <NetworkAvatar connectorIcon={activeAccount?.connectorIcon || ""} />
              <div>
                <div className="flex items-center gap-4">
                  <p className="font-semibold">{shortenAddress(activeAccount?.address || "")}</p>
                </div>
              </div>
            </div>
            <Button variant="ghost-clean" size="icon">
              <IoChevronForward size={24} />
            </Button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4 px-6 mt-6 flex-shrink-0">
          {!IS_MAINNET && (
            <Button
              variant="surface-secondary"
              size="md"
              radius="2xl"
              className="h-auto"
              onClick={() => {
                setIsFaucetOpen(true);
                setOpenDropdown(null);
              }}
            >
              <RiDropFill size={24} className="text-surfaces-primary" />
              <span>Faucet</span>
            </Button>
          )}
          <Button
            variant="surface-secondary"
            size="md"
            radius="2xl"
            className="h-auto"
            onClick={() => {
              router.push("/gold-bars");
              closeAllModals();
            }}
          >
            <GoldBarsIcon size={24} color="gold" />
            <span className="text-warning-500">
              {isLoading ? <Skeleton className="h-4 w-6 inline-block" /> : `${goldBars}`}
            </span>
          </Button>
        </div>

        {/* Navigation Sections - Scrollable */}
        <div className="flex-1 overflow-y-auto custom-scrollbar mt-8 min-h-0">
          <div className="space-y-8 px-6 pb-6">
            <div>
              <h3 className="text-small font-medium text-text-disabled mb-4">Other Nav Options</h3>
              <div className="grid grid-cols-5 gap-6">
                {[...navigationItems.other, ...additionalNavItems].map(item => {
                  if (item.type === "external") {
                    return (
                      <a
                        key={item.href}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col items-center gap-3 p-2"
                        onClick={closeAllModals}
                      >
                        {renderIcon(item.icon)}
                        <span className="text-xs text-center text-text-secondary">{item.label}</span>
                      </a>
                    );
                  }

                  if (item.type === "dropdown") {
                    return (
                      <DropdownMenu
                        key={item.label}
                        open={openDropdown === item.label}
                        onOpenChange={open => setOpenDropdown(open ? item.label : null)}
                      >
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="flex flex-col items-center gap-3 p-2 h-auto">
                            {renderIcon(item.icon)}
                            <span className="text-xs text-center text-text-secondary flex items-center gap-1">
                              {item.label}
                            </span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent variant="default" size="lg" radius="2xl">
                          <div className="space-y-2">
                            {item.children?.map(child => (
                              <DropdownMenuItem key={child.label} variant="large" className="cursor-pointer" asChild>
                                {child.isExternal ? (
                                  <a
                                    href={child.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 w-full"
                                    onClick={closeAllModals}
                                  >
                                    <span className="text-text-primary">{child.label}</span>
                                  </a>
                                ) : (
                                  <Link
                                    href={child.href}
                                    className="flex items-center gap-3 w-full"
                                    onClick={closeAllModals}
                                  >
                                    <span className="text-text-primary">{child.label}</span>
                                  </Link>
                                )}
                              </DropdownMenuItem>
                            ))}
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    );
                  }

                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className="flex flex-col items-center gap-3 p-2"
                      onClick={closeAllModals}
                    >
                      {renderIcon(item.icon)}
                      <span className="text-xs text-center text-text-secondary">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Order History Section */}
            <div>
              <h3 className="text-small font-medium text-text-disabled mb-4">Order History</h3>
              <div className="grid grid-cols-5 gap-6">
                {orderHistoryNavigation.map((item: NavigationItem) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="flex flex-col items-center gap-3 p-2"
                    onClick={closeAllModals}
                  >
                    <item.icon size={24} className="text-text-secondary" />
                    <span className="text-xs text-center text-text-secondary">{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Account Section */}
            <div>
              <h3 className="text-small font-medium text-text-disabled mb-4">Account</h3>
              <div className="grid grid-cols-5 gap-6">
                {navigationItems.account.map(item => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="flex flex-col items-center gap-3 p-2"
                    onClick={closeAllModals}
                  >
                    <item.icon size={24} className="text-text-secondary" />
                    <span className="text-xs text-center text-text-secondary">{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        <FaucetModal open={isFaucetOpen} onOpenChange={setIsFaucetOpen} />
        <UserAccountModal open={isUserAccountOpen} onOpenChange={setIsUserAccountOpen} />
      </div>
    </DrawerContent>
  );
}
